import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { isTokenExpired, setSessionExpiredHandler } from '../services/sessionGuard';

const API_URL = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'osw_customer_token';
const USER_KEY  = 'osw_customer';
const REF_KEY   = 'osw_referral_code';

// Capture a referral code from the landing URL (?ref=CODE) and remember it until the user logs in
try {
  const ref = new URLSearchParams(window.location.search).get('ref');
  if (ref) localStorage.setItem(REF_KEY, ref.trim().toUpperCase());
} catch { /* no-op */ }

export interface Customer {
  id: number;
  first_name?: string;
  last_name?: string;
  name?: string;
  mobile: string;
  email?: string;
  avatar_url?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  role: string;
  isNew?: boolean;
  totalOrders?: number;
}

interface AuthContextType {
  customer: Customer | null;
  token: string | null;
  sessionExpired: boolean;
  isAuthenticated: boolean;
  sendOTP: (mobile: string) => Promise<{ success: boolean; error?: string }>;
  verifyOTP: (mobile: string, otp: string) => Promise<{ success: boolean; isNew?: boolean; error?: string }>;
  completeProfile: (data: { first_name: string; last_name?: string; email?: string; gender?: string }) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (data: { first_name?: string; last_name?: string; email?: string; gender?: string }) => Promise<{ success: boolean; error?: string }>;
  uploadAvatar: (file: File) => Promise<{ success: boolean; error?: string }>;
  setCustomer: (c: Customer) => void;
  logout: () => void;
}

const CustomerAuthContext = createContext<AuthContextType | undefined>(undefined);

export const CustomerAuthProvider = ({ children }: { children: ReactNode }) => {
  const [customer, setCustomerState] = useState<Customer | null>(() => {
    const saved = localStorage.getItem(USER_KEY);
    return saved ? JSON.parse(saved) : null;
  });

  // A stored token that has already expired is not a session. Restoring it made
  // the site come up signed in and then fail every authenticated call — an empty
  // cart, saves that did nothing — until the user signed out and back in.
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored && isTokenExpired(stored)) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      return null;
    }
    return stored;
  });

  // Mirrors the admin context: shown on the login screen so an expiry explains
  // itself rather than looking like a bug.
  const [sessionExpired, setSessionExpired] = useState(false);

  const sendOTP = useCallback(async (mobile: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        return { success: false, error: json.message || json.error || 'Failed to send OTP' };
      }
      return { success: true };
    } catch {
      return { success: false, error: 'Cannot connect to server' };
    }
  }, []);

  const verifyOTP = useCallback(async (
    mobile: string,
    otp: string
  ): Promise<{ success: boolean; isNew?: boolean; error?: string }> => {
    try {
      const referral_code = localStorage.getItem(REF_KEY) || undefined;
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp, referral_code }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        return { success: false, error: json.message || json.error || 'OTP verification failed' };
      }
      const { token: newToken, user } = json;
      setToken(newToken);
      setCustomerState(user);
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      localStorage.removeItem(REF_KEY); // consumed (attribution only applies to brand-new accounts)
      return { success: true, isNew: user.isNew };
    } catch {
      return { success: false, error: 'Cannot connect to server' };
    }
  }, []);

  const completeProfile = useCallback(async (
    data: { first_name: string; last_name?: string; email?: string; gender?: string }
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_URL}/api/customers/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentToken}` },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok || !json.success) return { success: false, error: json.error || 'Failed to update profile' };
      const updated = json.user as Customer;
      setCustomerState(updated);
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return { success: true };
    } catch {
      return { success: false, error: 'Cannot connect to server' };
    }
  }, []);

  const updateProfile = useCallback(async (
    data: { first_name?: string; last_name?: string; email?: string; gender?: string }
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_URL}/api/customers/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentToken}` },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok || !json.success) return { success: false, error: json.error || 'Failed to update profile' };
      const updated = json.user as Customer;
      setCustomerState(updated);
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return { success: true };
    } catch {
      return { success: false, error: 'Cannot connect to server' };
    }
  }, []);

  const uploadAvatar = useCallback(async (file: File): Promise<{ success: boolean; error?: string }> => {
    try {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await fetch(`${API_URL}/api/customers/profile/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentToken}` },
        body: formData,
      });
      const json = await res.json();
      if (!res.ok || !json.success) return { success: false, error: json.error || 'Upload failed' };
      const updated = json.user as Customer;
      setCustomerState(updated);
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return { success: true };
    } catch {
      return { success: false, error: 'Cannot connect to server' };
    }
  }, []);

  const setCustomer = useCallback((c: Customer) => {
    setCustomerState(c);
    localStorage.setItem(USER_KEY, JSON.stringify(c));
  }, []);

  const logout = useCallback(() => {
    setCustomerState(null);
    setToken(null);
    setSessionExpired(false);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const endSession = useCallback(() => {
    setCustomerState(null);
    setToken(null);
    setSessionExpired(true);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  // Catch the expiry on a clock, the way the admin context already does — a tab
  // left open overnight shouldn't wait for a failed click to notice.
  useEffect(() => {
    const check = () => {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored && isTokenExpired(stored)) endSession();
    };
    check();
    const interval = setInterval(check, 60_000);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', check);
    };
  }, [endSession]);

  // And catch what a clock can't predict: a token the server rejects because the
  // signing secret was rotated, or the session was revoked.
  useEffect(() => {
    setSessionExpiredHandler('customer', endSession);
    return () => setSessionExpiredHandler('customer', null);
  }, [endSession]);

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        token,
        sessionExpired,
        isAuthenticated: !!customer && !!token,
        sendOTP,
        verifyOTP,
        completeProfile,
        updateProfile,
        uploadAvatar,
        setCustomer,
        logout,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be inside CustomerAuthProvider');
  return ctx;
};
